import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { CalendarFestival, CricketFixture } from '@/types/matchPushes';

/**
 * IST helpers for the cricket calendar. IST is UTC+05:30 with no DST, so the
 * fixed offset is used and the result does not depend on the browser's time
 * zone (same approach as analyticsWindow.ts).
 */
export const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** YYYY-MM-DD of the IST day containing the instant `ms`. */
export const istDayKey = (ms: number): string =>
  new Date(ms + IST_OFFSET_MS).toISOString().slice(0, 10);

/** The instant an IST day starts (00:00 IST), from 'YYYY-MM-DD'. */
export const istDayStart = (yyyyMmDd: string): Date => new Date(`${yyyyMmDd}T00:00:00+05:30`);

/** 'HH:mm' IST of an ISO instant. */
export const istClock = (iso: string): string =>
  new Date(new Date(iso).getTime() + IST_OFFSET_MS).toISOString().slice(11, 16);

/** '22 Oct, 12:30 IST' for an ISO instant. */
export const istDateTime = (iso: string): string => {
  const shifted = new Date(new Date(iso).getTime() + IST_OFFSET_MS);
  return `${shifted.getUTCDate()} ${MONTHS[shifted.getUTCMonth()]}, ${istClock(iso)} IST`;
};

/** 'Thu 22 Oct' for a 'YYYY-MM-DD' day (a calendar label, no time zone involved). */
export const dayLabel = (yyyyMmDd: string): string => {
  const d = new Date(`${yyyyMmDd}T00:00:00Z`);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

/** Day heading: 'Thu 22 Oct · today' / '· tomorrow' / '· yesterday'. */
export const dayHeading = (yyyyMmDd: string, nowMs: number = Date.now()): string => {
  const today = istDayKey(nowMs);
  const diff = Math.round(
    (new Date(`${yyyyMmDd}T00:00:00Z`).getTime() - new Date(`${today}T00:00:00Z`).getTime()) /
      DAY_MS
  );
  const suffix =
    diff === 0 ? ' · today' : diff === 1 ? ' · tomorrow' : diff === -1 ? ' · yesterday' : '';
  return `${dayLabel(yyyyMmDd)}${suffix}`;
};

/**
 * A date picker value showing the IST wall clock of an instant. The picker
 * works in the browser's zone, so the wall-clock digits are kept and the zone
 * is ignored; `fromIstWallClock` reverses it.
 */
export const toIstWallClock = (iso: string): Dayjs =>
  dayjs(new Date(new Date(iso).getTime() + IST_OFFSET_MS).toISOString().slice(0, 16));

/** ISO instant for the wall-clock digits of a picker value read as IST. */
export const fromIstWallClock = (value: Dayjs): string =>
  new Date(`${value.format('YYYY-MM-DDTHH:mm')}:00+05:30`).toISOString();

export interface CalendarDay {
  /** YYYY-MM-DD, IST. */
  day: string;
  festivals: CalendarFestival[];
  fixtures: CricketFixture[];
}

/**
 * Groups fixtures (by the IST day of `startsAt`) and festivals (by `startsOn`)
 * into ascending days; fixtures in a day are ordered by start time.
 */
export function groupByIstDay(
  fixtures: CricketFixture[],
  festivals: CalendarFestival[] = []
): CalendarDay[] {
  const days = new Map<string, CalendarDay>();
  const get = (day: string): CalendarDay => {
    let entry = days.get(day);
    if (!entry) {
      entry = { day, festivals: [], fixtures: [] };
      days.set(day, entry);
    }
    return entry;
  };
  for (const fixture of fixtures) {
    get(istDayKey(new Date(fixture.startsAt).getTime())).fixtures.push(fixture);
  }
  for (const festival of festivals) {
    get(festival.startsOn).festivals.push(festival);
  }
  const out = [...days.values()].sort((a, b) => a.day.localeCompare(b.day));
  for (const entry of out) {
    entry.fixtures.sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  }
  return out;
}

/** Flag emoji for an ISO-2 country code ('IN' -> flag of India); '' when not a code. */
export function countryFlag(code: string | null | undefined): string {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return '';
  return String.fromCodePoint(
    ...code
      .toUpperCase()
      .split('')
      .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
}

/** English country name for an ISO-2 code, or the code when unknown. */
export function countryName(code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/**
 * The settings card's warning for tracked countries that no team key in the
 * backend's team list stands for (no match is tracked for them); null when
 * there are none.
 */
export function countriesWithoutTeamsWarning(codes: string[]): string | null {
  if (!codes.length) return null;
  const names = codes.map((c) => `${countryName(c)} (${c})`).join(', ');
  return `No team in the team list plays for ${names}, so no match is tracked or pushed for players there. A developer adds its team key in team-countries.ts.`;
}

/** 'India players' / 'India, Pakistan players'; '—' when there is no audience. */
export function audienceLabel(codes: string[]): string {
  if (!codes.length) return '—';
  return `${codes.map(countryName).join(', ')} players`;
}
